<div class="px-2 py-3">
  <!-- Header -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex items-start gap-3">
      <span class="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400">
        <lucide-icon [name]="Flame" class="h-5 w-5"></lucide-icon>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ board.meta.title }}</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ board.meta.subtitle }}</p>
      </div>
    </div>
    <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
      <lucide-icon [name]="Sparkles" class="h-3.5 w-3.5"></lucide-icon>
      Last run {{ board.meta.lastRun }}
    </span>
  </div>

  <!-- Header totals -->
  <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="text-2xl font-semibold text-gray-800 dark:text-white/90">{{ board.meta.quotesProcessed | number }}</div>
      <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">Quotes processed</div>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="text-2xl font-semibold text-gray-800 dark:text-white/90">{{ board.meta.venues }}</div>
      <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">Venues</div>
    </div>
    <div class="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/[0.06]">
      <div class="text-2xl font-semibold text-amber-700 dark:text-amber-300">{{ board.meta.highValueProspects | number }}</div>
      <div class="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">High value, £15k+</div>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="text-2xl font-semibold text-gray-800 dark:text-white/90">{{ board.meta.topToCallFirst }}</div>
      <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">To call first</div>
    </div>
  </div>

  <!-- Data analysis box -->
  <div class="mt-4 rounded-2xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-500/30 dark:bg-blue-500/[0.06]">
    <div class="flex items-center gap-2 text-sm font-semibold text-blue-800 dark:text-blue-300">
      <lucide-icon [name]="Sparkles" class="h-4 w-4"></lucide-icon>
      Data analysis
    </div>
    <ul class="mt-2 space-y-1.5">
      @for (comment of board.analysisComments; track comment) {
        <li class="flex gap-2 text-sm text-blue-900/80 dark:text-blue-100/80">
          <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400 dark:bg-blue-300"></span>
          <span>{{ comment }}</span>
        </li>
      }
    </ul>
  </div>

  <!-- Venue filter chips -->
  <div class="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by venue">
    @for (venue of board.venueFilter; track venue) {
      <button
        type="button"
        class="rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
        [ngClass]="isVenueSelected(venue)
          ? 'border-brand-500 bg-brand-500 text-white dark:border-brand-500 dark:bg-brand-500'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10'"
        [attr.aria-pressed]="isVenueSelected(venue)"
        (click)="selectVenue(venue)">
        {{ venue }}
      </button>
    }
  </div>

  <!-- Board: ranked list + detail panel -->
  <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
    <!-- Ranked list -->
    <div class="space-y-2 lg:col-span-7">
      @for (lead of visibleLeads; track lead.id) {
        <button
          type="button"
          class="relative flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left transition-colors dark:bg-white/[0.03]"
          [ngClass]="{
            'border-amber-300 ring-1 ring-amber-200 dark:border-amber-500/40 dark:ring-amber-500/20': lead.highValue,
            'border-gray-200 dark:border-gray-800': !lead.highValue,
            'outline outline-2 outline-brand-500': isLeadSelected(lead)
          }"
          [attr.data-lead-id]="lead.id"
          (click)="selectLead(lead)">
          @if (lead.highValue) {
            <span class="absolute inset-y-0 left-0 w-1 rounded-l-2xl bg-amber-400 dark:bg-amber-500"></span>
          }

          <!-- Rank -->
          <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700 dark:bg-white/5 dark:text-white/80">
            {{ lead.rank }}
          </span>

          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-semibold text-gray-800 dark:text-white/90">{{ lead.name }}</span>
              @if (lead.highValue) {
                <span class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                  ★ High value
                </span>
              }
              <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="bandClasses(lead.band)">
                {{ lead.bandLabel }}
              </span>
            </div>

            <div class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {{ lead.venue }} · {{ lead.date }}
            </div>

            <p class="mt-1.5 text-sm text-gray-600 dark:text-white/70">{{ lead.reason }}</p>

            <!-- Signal tags -->
            <div class="mt-2 flex flex-wrap gap-1.5">
              @for (signal of lead.signals; track signal[1]) {
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="signalClasses(signal[0])">
                  {{ signal[1] }}
                </span>
              }
            </div>
          </div>

          <!-- Score (headline ranking number) + estimated total -->
          <div class="flex shrink-0 flex-col items-end gap-1.5">
            <div class="flex flex-col items-center rounded-xl bg-brand-50 px-3 py-1.5 dark:bg-brand-500/10">
              <span class="text-2xl font-bold leading-none text-brand-600 dark:text-brand-400">{{ lead.score }}</span>
              <span class="mt-1 text-[10px] font-semibold uppercase tracking-wide text-brand-700/70 dark:text-brand-300/70">Score</span>
            </div>
            <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ lead.total }}</div>
          </div>
        </button>
      } @empty {
        <div class="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
          No leads for this venue.
        </div>
      }
    </div>

    <!-- Detail panel -->
    <div class="lg:col-span-5">
      <div
        class="sticky top-4 rounded-2xl border bg-white p-5 dark:bg-white/[0.03]"
        [ngClass]="selectedLead.highValue
          ? 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-500/40 dark:ring-amber-500/20'
          : 'border-gray-200 dark:border-gray-800'">
        <!-- Panel header -->
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ selectedLead.name }}</h3>
              @if (selectedLead.highValue) {
                <span class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                  ★ High value
                </span>
              }
            </div>
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ selectedLead.venue }} · {{ selectedLead.date }}</p>
          </div>
          <div class="flex shrink-0 flex-col items-end gap-1.5">
            <div class="flex items-baseline gap-1.5 rounded-xl bg-brand-50 px-3 py-1.5 dark:bg-brand-500/10">
              <span class="text-xl font-bold leading-none text-brand-600 dark:text-brand-400">{{ selectedLead.score }}</span>
              <span class="text-[10px] font-semibold uppercase tracking-wide text-brand-700/70 dark:text-brand-300/70">Score</span>
            </div>
            <span class="rounded-full px-2.5 py-1 text-xs font-medium" [ngClass]="bandClasses(selectedLead.band)">
              {{ selectedLead.bandLabel }}
            </span>
          </div>
        </div>

        <!-- Signals -->
        <div class="mt-3 flex flex-wrap gap-1.5">
          @for (signal of selectedLead.signals; track signal[1]) {
            <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="signalClasses(signal[0])">
              {{ signal[1] }}
            </span>
          }
        </div>

        <!-- Profile -->
        <p class="mt-4 text-sm leading-relaxed text-gray-600 dark:text-white/70">{{ selectedLead.profile }}</p>

        <!-- Budget gap -->
        <div class="mt-4 grid grid-cols-2 gap-3">
          <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ selectedLead.gapLeft[0] }}</div>
            <div class="mt-0.5 text-sm font-semibold text-gray-800 dark:text-white/90">{{ selectedLead.gapLeft[1] }}</div>
          </div>
          <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ selectedLead.gapRight[0] }}</div>
            <div class="mt-0.5 text-sm font-semibold text-gray-800 dark:text-white/90">{{ selectedLead.gapRight[1] }}</div>
          </div>
        </div>
        <p class="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          <span class="font-medium text-gray-600 dark:text-white/70">{{ selectedLead.fitGrade }}.</span>
          {{ selectedLead.fitNote }}
        </p>

        <!-- Why this lead -->
        <div class="mt-4">
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Why this lead</h4>
          <ul class="mt-2 space-y-1.5">
            @for (point of selectedLead.why; track point) {
              <li class="flex gap-2 text-sm text-gray-600 dark:text-white/70">
                <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/30"></span>
                <span>{{ point }}</span>
              </li>
            }
          </ul>
        </div>

        <!-- Suggested action -->
        <div class="mt-4 flex items-start gap-2 rounded-xl border border-brand-200 bg-brand-50/60 p-3 dark:border-brand-500/30 dark:bg-brand-500/[0.08]">
          <lucide-icon [name]="Phone" class="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400"></lucide-icon>
          <div>
            <div class="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">Suggested action</div>
            <p class="mt-0.5 text-sm text-gray-700 dark:text-white/80">{{ selectedLead.action }}</p>
          </div>
        </div>

        <!-- Draft message -->
        <div class="mt-4">
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Draft message</h4>
          <div class="mt-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
            <div class="text-sm font-medium text-gray-800 dark:text-white/90">{{ selectedLead.draftSubj }}</div>
            <p class="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-white/70">{{ selectedLead.draft }}</p>
          </div>
        </div>

        <!-- Quote breakdown -->
        <div class="mt-4">
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Quote</h4>
          <dl class="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
            @for (row of selectedLead.quote; track row[0]) {
              <div class="flex items-center justify-between gap-3 px-3 py-2">
                <dt class="text-xs text-gray-500 dark:text-gray-400">{{ row[0] }}</dt>
                <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ row[1] }}</dd>
              </div>
            }
          </dl>
        </div>
      </div>
    </div>
  </div>
</div>
